import {
  getFormProps,
  getInputProps,
  getTextareaProps,
  useForm,
} from "@conform-to/react";
import { CloseButton } from "@headlessui/react";
import { Trans, t } from "@lingui/macro";
import { InfoCircle } from "@winamp/icons/InfoCircle.js";
import { invariantResponse } from "@winamp/invariant";
import { uploadFile } from "@winamp/media-upload/upload-file.js";
import { useUploads } from "@winamp/media-upload/use-uploads.js";
import { redirectWithSuccess } from "@winamp/toast/index.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { Dialog } from "@winamp/ui/components/Dialog/Dialog.js";
import { DialogActions } from "@winamp/ui/components/Dialog/DialogActions.js";
import { DialogBody } from "@winamp/ui/components/Dialog/DialogBody.js";
import { DialogTitle } from "@winamp/ui/components/Dialog/DialogTitle.js";
import { ErrorMessages } from "@winamp/ui/components/Fieldset/ErrorMessages.js";
import { Field } from "@winamp/ui/components/Fieldset/Field.js";
import { Fieldset } from "@winamp/ui/components/Fieldset/Fieldset.js";
import { FormErrorsMessage } from "@winamp/ui/components/Fieldset/FormErrorsMessage.js";
import { Label } from "@winamp/ui/components/Fieldset/Label.js";
import { Input } from "@winamp/ui/components/Input/Input.js";
import { Message } from "@winamp/ui/components/Message/Message.js";
import { MessageContent } from "@winamp/ui/components/Message/MessageContent.js";
import { MessageDescription } from "@winamp/ui/components/Message/MessageDescription.js";
import { MessageTitle } from "@winamp/ui/components/Message/MessageTitle.js";
import { Tooltip } from "@winamp/ui/components/Tooltip/Tooltip.js";
import { TooltipContent } from "@winamp/ui/components/Tooltip/TooltipContent.js";
import { TooltipTrigger } from "@winamp/ui/components/Tooltip/TooltipTrigger.js";
import { getValibotConstraint, parseWithValibot } from "conform-to-valibot";
import { type ComponentProps, useEffect, useRef, useState } from "react";
import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  redirect,
} from "react-router";
import { useFetcher, useSearchParams } from "react-router";
import * as v from "valibot";
import { ImageUpload } from "~/components/ImageUpload";
import { TitleBeautifier } from "~/components/TitleBeautifier";
import {
  type CollectionEditableFields,
  isCollectionEditable,
} from "~/models/collection";
import { requireAuth } from "~/modules/auth/auth.server";
import { useBytescaleConfig } from "~/modules/bytescale/bytescale";
import { getIsUploading } from "~/modules/bytescale/get-is-uploading";
import { patchForm } from "~/modules/conform/helpers";
import { getImageSchema } from "~/modules/conform/schemas";
import { $contextPath } from "~/modules/routing/context-path.server";
import { useContextPath } from "~/modules/routing/use-context-path";
import { OpenAPI, getResponseErrors } from "~/modules/sdk/client.server";

const getUpdateCollectionSchema = () =>
  v.object({
    name: v.pipe(
      v.string(t`The collection name is required.`),
      v.minLength(1, t`The collection name is required.`),
    ),
    cover: v.optional(getImageSchema()),
    description: v.optional(v.string()),
  });

export async function action({ request, params }: ActionFunctionArgs) {
  const { accessToken } = await requireAuth(request);

  invariantResponse(params.id, "An collectionUid is required");
  invariantResponse(
    params.artistId && params.artistId !== "all",
    "An artist must be selected.",
  );

  const formData = await request.formData();
  const submission = parseWithValibot(formData, {
    schema: getUpdateCollectionSchema(),
  });
  invariantResponse(params.id, "An collectionUid is required");

  if (submission.status !== "success") {
    return submission.reply({
      resetForm: false,
      formErrors: [t`The collection could not be updated due to invalid data.`],
    });
  }

  const { error } = await OpenAPI.PATCH(
    "/api/artists/{artistUid}/collections/{collectionUid}",
    {
      params: {
        path: {
          artistUid: params.artistId,
          collectionUid: params.id,
        },
      },
      body: {
        name: submission.value.name,
        description: submission.value.description ?? null,
        coverPath: submission.value.cover?.currentFilename
          ? (submission.value.cover?.currentFilename ?? null)
          : null,
      },
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  if (error) {
    const { fieldErrors, formErrors } = getResponseErrors(error);

    return submission.reply({
      resetForm: false,
      fieldErrors,
      formErrors: fieldErrors
        ? formErrors
        : (formErrors ?? [t`An unexpected error occured.`]),
    });
  }

  const url = new URL(request.url);
  const redirectTo = url.searchParams.get("redirectTo");

  return redirectWithSuccess(
    redirectTo ?? (await $contextPath(request, params, "/media/collections")),
    {
      title: t`Collection updated!`,
      message: t`The collection has been updated successfully.`,
    },
  );
}

export type SearchParams = {
  init?: boolean;
  redirectTo?: string | null;
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  await requireAuth(request);

  invariantResponse(
    params.artistId && params.artistId !== "all",
    "An artist must be selected.",
  );
  invariantResponse(params.id, "Collection ID is required", {
    status: 404,
  });

  const url = new URL(request.url);
  const isInitRequest = url.searchParams.get("init");
  const redirectTo = url.searchParams.get("redirectTo");

  if (!isInitRequest) {
    if (!redirectTo) {
      throw redirect(
        await $contextPath(
          request,
          params,
          "/media/collections/:id",
          { id: params.id },
          { dialog: "update-collection" },
        ),
      );
    }

    const [path, query] = redirectTo?.split("?") ?? [];
    const queryParameters = new URLSearchParams(query);
    queryParameters.set("dialog", "update-collection");
    queryParameters.set("redirectTo", redirectTo);

    throw redirect(`${path}?${queryParameters.toString()}`);
  }

  return null;
}

interface IDialogCollectionUpdateProps
  extends Omit<
    ComponentProps<typeof Dialog>,
    "children" | "open" | "onClose" | "unmount"
  > {
  isOpen?: boolean;
  setIsOpen: (value: boolean) => void;
  collection: CollectionEditableFields;
}

export function DialogCollectionUpdate({
  isOpen,
  setIsOpen,
  collection,
  ...props
}: IDialogCollectionUpdateProps) {
  const contextPath = useContextPath();
  const titleBeautifierRef = useRef<HTMLButtonElement>(null);
  const bytescaleConfig = useBytescaleConfig();
  const [searchParams] = useSearchParams();
  const [uploadingFile, setUploadingFile] = useState<string | null>(null);
  const { uploads, cancelUpload, removeUpload } = useUploads(
    "update-collection-cover",
  );
  const activeUpload = uploads.find(
    (upload) => upload.uploadId === uploadingFile,
  );
  const isUploading = getIsUploading(activeUpload);

  const isEditable = isCollectionEditable(collection);

  const dataFetcher = useFetcher<typeof loader>({
    key: "updateCollectionDialogData",
  });

  const redirectTo = searchParams.get("redirectTo");
  const formAction = collection
    ? contextPath(
        "/media/collections/:id/update",
        { id: collection.uid },
        redirectTo ? { redirectTo } : undefined,
      )
    : undefined;
  const fetcher = useFetcher<typeof action>({
    key: "updateCollectionDialog",
  });
  const isPending = fetcher.state !== "idle";
  const schema = getUpdateCollectionSchema();

  useEffect(() => {
    if (isOpen && collection.uid) {
      dataFetcher.load(
        contextPath(
          "/media/collections/:id/update",
          { id: collection.uid },
          { init: true },
        ),
      );
    }
  }, [collection.uid, isOpen]);

  useEffect(() => {
    // Close after successful action
    if (fetcher.data?.status === "success") {
      setIsOpen?.(false);
    }
  }, [fetcher.data]);

  const [form, fields] = useForm({
    defaultValue: {
      description: collection.info.description.value ?? "",
      name: collection.info.title.value ?? "",
      cover: {
        currentFilename: collection.info.coverPath.value,
      },
    },
    lastResult: fetcher.data,
    constraint: getValibotConstraint(schema),
    shouldRevalidate: "onBlur",
    onValidate({ formData }) {
      return parseWithValibot(formData, { schema });
    },
  });

  const close = () => {
    if (!isPending) {
      form.reset();
      setIsOpen?.(false);
      if (activeUpload?.uploadId) {
        removeUpload?.(activeUpload.uploadId);
      }
    }
  };

  const onCoverDrop = async (file: File) => {
    const uploadId = file.name;
    setUploadingFile(uploadId);
    const result = await uploadFile({
      key: "update-collection-cover",
      file: file,
      uploadId,
      publicKey: bytescaleConfig.publicKey,
      folderPath: bytescaleConfig.folderPath.media,
      accountId: bytescaleConfig.accountId,
      canUpload: bytescaleConfig.canUpload,
    });

    if (result?.originalFileName) {
      const currentCoverImageFilename = result.filePath.split("/").pop();
      patchForm(form, {
        cover: {
          currentFilename: currentCoverImageFilename,
          originalFilename: result.originalFileName,
        },
      });
    }
  };

  const coverFieldset = fields.cover.getFieldset();

  return (
    <Dialog {...props} size="xl" open={isOpen} onClose={close}>
      <DialogTitle>
        <Trans>Update "{collection.info.title.value}"</Trans>
      </DialogTitle>

      <DialogBody>
        <fetcher.Form
          action={formAction}
          method="post"
          {...getFormProps(form)}
          className="flex flex-col gap-8"
        >
          <Fieldset className="flex flex-col gap-4" disabled={isPending}>
            <Field disabled={collection.info.title.isLocked}>
              <Label htmlFor={fields.name.id}>
                <Trans>Collection name</Trans>
              </Label>
              <div data-slot="control" className="relative">
                <Input
                  placeholder={t`Collection name`}
                  {...getInputProps(fields.name, { type: "text" })}
                  key={fields.name.key}
                  className="[&>input]:pr-8"
                  onBlur={() => {
                    titleBeautifierRef.current?.click();
                  }}
                />

                <TitleBeautifier
                  fetcherKey={collection.uid}
                  onChange={(title) => {
                    if (isPending) return;
                    form.update({
                      name: fields.name.name,
                      value: title,
                    });
                  }}
                  value={fields.name.value}
                  ref={titleBeautifierRef}
                />
              </div>
              <ErrorMessages errors={fields.name.errors} />
            </Field>

            <input
              {...getInputProps(coverFieldset.originalFilename, {
                type: "hidden",
              })}
              key={coverFieldset.originalFilename.key}
            />
            <Field disabled={collection.info.coverPath.isLocked}>
              <Label htmlFor={coverFieldset.currentFilename.id}>
                <Trans>Collection cover</Trans>
              </Label>
              <ImageUpload
                meta={coverFieldset.currentFilename}
                className="sm:w-1/2 aspect-square"
                disabled={collection.info.title.isLocked}
                onFileDrop={onCoverDrop}
                upload={activeUpload}
                cancelUpload={cancelUpload}
                removeUpload={removeUpload}
                privateImageOptions={{
                  transformationParams: { w: 768, h: 768 },
                }}
              />
              <ErrorMessages errors={coverFieldset.currentFilename.errors} />
            </Field>

            <Field disabled={collection.info.description.isLocked}>
              <Label
                htmlFor={fields.description.id}
                className="inline-flex items-center gap-2"
              >
                <Trans>
                  Description{" "}
                  <span className="text-secondary font-normal lowercase">
                    (optional)
                  </span>
                </Trans>
                <Tooltip>
                  <TooltipTrigger>
                    <InfoCircle />
                  </TooltipTrigger>
                  <TooltipContent className="w-[240px]">
                    <Trans>
                      This description is used as the subtitle for your release.
                    </Trans>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <Input
                {...getTextareaProps(fields.description)}
                key={fields.description.key}
                placeholder={t({
                  message: "Add an optional description.",
                })}
              />
              <ErrorMessages errors={fields.description.errors} />
            </Field>
          </Fieldset>
        </fetcher.Form>
        <FormErrorsMessage errors={form.errors} />
        {!isEditable && (
          <Message type="warning" className="mt-4">
            <MessageContent>
              <MessageTitle>
                <Trans>This collection is not editable.</Trans>
              </MessageTitle>
              <MessageDescription>
                <Trans>
                  This collection is currently used in one of our services and
                  therefore cannot be altered.
                </Trans>
              </MessageDescription>
            </MessageContent>
          </Message>
        )}
      </DialogBody>

      <DialogActions>
        {isEditable ? (
          <>
            <CloseButton
              as={Button}
              variant="ghost"
              onClick={close}
              disabled={isPending}
            >
              <Trans>Cancel</Trans>
            </CloseButton>
            <Button
              form={form.id}
              type="submit"
              disabled={isPending || isUploading}
              loading={isPending}
            >
              <Trans>Update collection</Trans>
            </Button>
          </>
        ) : (
          <CloseButton
            as={Button}
            variant="ghost"
            onClick={close}
            disabled={isPending}
          >
            <Trans>Close</Trans>
          </CloseButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
